import { useMutation } from 'data-fetching-client';
import { generatePath, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { NEW_ACTION } from 'automation-ui-flow-core/canvas/SpecialActionIds';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import { useFeaturePermission } from 'automation-ui-permissions/feature-permissions/hooks/featurePermissionHooks';
import { EDIT_ACTIONS } from 'automation-ui-permissions/feature-permissions/constants/WorkflowManagementFeatures';
import { joinPathAndQuery } from 'automation-ui-routing/utils';
import { setAiGeneratedActions } from 'automation-ui-canvas/actions/AiAssistantActions';
import { updateFlowLocally } from 'automation-ui-canvas/actions/FlowActions';
import GenerateButton from 'automation-ui-canvas/components/panels/ai-assistant-panel/GenerateButton';
import { AI_ASSISTANT_GENERATE_ASSET } from 'automation-ui-canvas/constants/AbsolutePaths';
import { AI_GENERATE_ACTIONS, AI_GENERATE_ACTIONS_FIELD_NAME } from 'automation-ui-canvas/queries/opsHubAiQueries';
import { appendActionsToFlow, appendActionsToEdge, formatAiGeneratedActions } from 'automation-ui-canvas/utils/aiAssistantUtils';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import { jsx as _jsx } from "react/jsx-runtime";
const GenerateActionsButton = ({
  edgeId,
  flow,
  flowId,
  onError,
  userInput,
  isRegenerating
}) => {
  const history = useHistory();
  const trackEvent = useTrackEvent();
  const dispatch = useDispatch();

  // TODO - @mlegocki: pass `flow` from AI-Assistant panel root and implement loading state
  const objectTypeId = flow && flow.getObjectTypeId();
  const {
    hasFeatureAccess: hasEditActionsAccess
  } = useFeaturePermission({
    feature: EDIT_ACTIONS
  });
  const [generateActions, {
    error,
    loading
  }] = useMutation(AI_GENERATE_ACTIONS, {});
  const handleGenerateActions = () => {
    var _flow$nextAvailableAc;
    // reset any existing errors on action generation attempt
    onError(false);
    trackEvent(Trackables.CLICK_TO_GENERATE_ASSET, Object.assign({}, isRegenerating ? {
      action: 'clicked to re-generate asset'
    } : {}, {
      aiPrompt: userInput,
      assetType: 'AI Generated Workflow Action',
      flowId,
      destination: edgeId
    }));
    const startingActionId = (_flow$nextAvailableAc = flow === null || flow === void 0 ? void 0 : flow.nextAvailableActionId) !== null && _flow$nextAvailableAc !== void 0 ? _flow$nextAvailableAc : 1;
    generateActions({
      variables: {
        objectTypeId: objectTypeId,
        userInput,
        startingActionId
      }
    }).then(({
      data
    }) => {
      const results = data && data[AI_GENERATE_ACTIONS_FIELD_NAME];
      if (results) {
        const {
          actionEggs: generatedActions,
          firstActionId
        } = results;
        const hasGeneratedActions = Object.keys(generatedActions).length;
        if (hasGeneratedActions && flow) {
          const actionsToAppend = formatAiGeneratedActions(generatedActions);
          const actionIdToScrollTo = actionsToAppend[0];
          const flowWithAppendedActions = edgeId ? appendActionsToEdge({
            actionsToAppend,
            startingActionId: firstActionId !== null && firstActionId !== void 0 ? firstActionId : startingActionId,
            edgeId,
            flow: flow.removeAction(NEW_ACTION)
          }) : appendActionsToFlow({
            actionsToAppend,
            startingActionId: firstActionId !== null && firstActionId !== void 0 ? firstActionId : startingActionId,
            flow
          });
          onError(false);
          // TODO can we move any of these back down to GenerateButton?
          dispatch(updateFlowLocally(flowWithAppendedActions));
          dispatch(setAiGeneratedActions(results));
          history.push(joinPathAndQuery(generatePath(AI_ASSISTANT_GENERATE_ASSET, {
            assetType: 'actions',
            flowId
          }), {
            edgeId
          }), {
            scrollToAction: {
              actionId: actionIdToScrollTo.getActionId()
            }
          });
        } else {
          // if no actions generated from prompt, display error validation message
          onError(true);
        }
      }
    }).catch(err => {
      reportError({
        error: err
      });
      onError(true);
    });
  };
  return /*#__PURE__*/_jsx(GenerateButton, {
    buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "aiAssistantPanel.body.addActionsPanel.actionDescriptionSection.generateButton"
    }),
    regenerateButtonText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "aiAssistantPanel.body.addActionsPanel.actionDescriptionSection.regenerateButton"
    }),
    disabled: !hasEditActionsAccess,
    flowId: flowId,
    hasGenerateError: !!error,
    hasValidUserInput: !!userInput.length,
    isGenerateLoading: loading,
    onGenerateClick: handleGenerateActions,
    tooltipTitle: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "aiAssistantPanel.body.addActionsPanel.actionDescriptionSection.missingInputTooltip"
    }),
    isRegenerating: isRegenerating
  });
};
export default GenerateActionsButton;